import React, { useEffect, useState, useRef } from "react";
import { dsColors } from "../utils/dsTokens";
import { AiOutlineMenu } from "react-icons/ai";
import { useStateContext } from "../contexts/ContextProvider";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast, Icon } from "./ds";
import NotificationBell from "./notifications/NotificationBell";
import LiveDataStatus from "./LiveDataStatus";

const NavButton = ({ title, customFunc, icon, color, dotColor }) => (
  <button
    type="button"
    onClick={() => customFunc()}
    style={{ color }}
    className="relative text-xl rounded-full p-2 hover:bg-bg-3"
  >
    <span
      style={{ background: dotColor }}
      className="absolute inline-flex rounded-full h-2 w-2 right-2 top-2"
    />
    {icon}
  </button>
);

const Navbar = ({ onLoginClick }) => {
  const {
    currentColor,
    activeMenu,
    setActiveMenu,
    setScreenSize,
    screenSize,
    currentMode,
    setThemeSettings,
  } = useStateContext();

  const { isAuthenticated, user, logout, fetchUserData, sessionExpiry, brokerSessionExpiry } = useAuth();
  const navigate = useNavigate();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [showThemeSnackbar, setShowThemeSnackbar] = useState(false);

  //--> DS toast (replaces the removed MUI Snackbar). Bridge the legacy
  //    setShowThemeSnackbar(...) trigger so the existing call-site keeps working.
  const toast = useToast();
  useEffect(() => {
    if (showThemeSnackbar) {
      toast.info("Theme settings opened");
      setShowThemeSnackbar(false);
    }
  }, [showThemeSnackbar, toast]);

  // Session timer state for RAG indicator
  // Initialize to null so we don't display timeout text until we have a real value
  const [timeLeft, setTimeLeft] = useState(null);
  const [progress, setProgress] = useState(100);
  const [progressColor, setProgressColor] = useState(dsColors.success); // --success default
  // Donut chart params
  const RING_R = 16;
  const RING_CIRC = 2 * Math.PI * RING_R;

  // brokerSessionExpiry is now managed in AuthContext


  useEffect(() => {
    if (!isAuthenticated || !brokerSessionExpiry) {
        if (!brokerSessionExpiry) {
            setTimeLeft(null); // Ensure timer hidden if no broker session
        }
        return;
    }

    const calculateTime = () => {
        const now = Date.now();
        const difference = brokerSessionExpiry - now;
        
        if (difference <= 0) {
            setTimeLeft(0);
            setProgress(0);
            return;
        }

        const secondsLeft = Math.floor(difference / 1000);
        setTimeLeft(secondsLeft);
        
        // Visual duration: Max 24 hours (86400s) because broker sessions are long
        // But for visual urgency, maybe stick to 1 hour (3600s) scale?
        // If > 1 hour, show full circle?
        const MAX_VISUAL_DURATION = 60 * 60; // 1 hour for visual progress scaling
        
        // If > 1 hour, keep 100%. If < 1 hour, start shrinking.
        let progressPercent = 100;
        if (secondsLeft < MAX_VISUAL_DURATION) {
             progressPercent = Math.min(100, (secondsLeft / MAX_VISUAL_DURATION) * 100);
        }
        
        setProgress(progressPercent);

        // RAG Logic
        if (secondsLeft < 300) { // < 5 mins
            setProgressColor(dsColors.danger); // --danger
        } else if (secondsLeft < 900) { // < 15 mins
            setProgressColor(dsColors.warning); // --warning
        } else {
            setProgressColor(dsColors.success); // --success
        }
    };

    calculateTime(); // Initial call
    const timer = setInterval(calculateTime, 1000);

    return () => clearInterval(timer);
  }, [isAuthenticated, brokerSessionExpiry]);

  const handleActiveMenu = () => {
    setActiveMenu(!activeMenu);
  };

  const handleThemeClick = () => {
    setThemeSettings(true);
  };

  const handleProfileClick = async () => {
    try {
      // Fetch fresh user data before navigating
      await fetchUserData();
      // Then navigate to profile page
      navigate("/profile");
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      // Navigate anyway even if there's an error
      navigate("/profile");
      setIsUserMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsUserMenuOpen(false);
  };

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  // Handle clicks outside user menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);

    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [setScreenSize]);

  useEffect(() => {
    if (screenSize <= 900) {
      setActiveMenu(false);
    } else {
      setActiveMenu(true);
    }
  }, [screenSize, setActiveMenu]);

  return (
    // Layout container only — the surface (glass bg + blur + border + sticky) is
    // owned by the wrapper in MainLayout/WithLayout; re-declaring it here was a
    // double background + double blur + double border.
    <div className="flex justify-between items-center p-2 w-full">
      {/* NavButton with menu icon - only visible on small screens */}
      <NavButton
        customFunc={handleActiveMenu}
        color={currentColor}
        icon={<AiOutlineMenu />}
      />

      <div className="flex items-center gap-2">
        {/* #44: live-data status — steady healthy renders nothing; an outage shows
            the pill; after a visible outage ends a brief quiet "Reconnected" line
            appears and vanishes on its own. Never a permanent light. Sits beside
            the bell (same attention zone) rather than inside it, so the bell
            keeps meaning exactly one thing: your trading notifications. */}
        {isAuthenticated && <LiveDataStatus />}

        {/* Notification bell — only render when authenticated */}
        {isAuthenticated && <NotificationBell />}

        {isAuthenticated ? (
          <div className="relative" ref={userMenuRef}>
            <div
              className={`flex items-center gap-2 cursor-pointer p-1 px-3 rounded-lg transition-all duration-200 group relative
                ${
                  isUserMenuOpen
                    ? "bg-bg-2 text-fg-1 shadow-lg"
                    : "hover:bg-bg-3"
                }`}
              onClick={handleUserMenuToggle}
            >
              {/* Welcome section with avatar, donut ring, warning, and user info grouped */}
              <div className="flex items-center gap-2">
                <div className="relative flex items-center justify-center" style={{height: '32px', width: '32px'}}>
                  {/* Donut ring around avatar icon - thinner, modern, tightly aligned */}
                  <div
                    className="rounded-full flex items-center justify-center"
                    style={{
                      background: 'var(--bg-3)',
                      border: isUserMenuOpen ? '2px solid var(--color-accent)' : '2px solid var(--border)',
                      width: '24px',
                      height: '24px',
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    <Icon name="user" size={16} style={{ color: 'var(--fg-2)' }} />
                  </div>
                  {isAuthenticated && timeLeft !== null && (
                    <svg
                      className="absolute top-0 left-0 pointer-events-none"
                      width="32" height="32" viewBox="0 0 32 32"
                      style={{ zIndex: 1 }}
                    >
                      <circle cx="16" cy="16" r="14" fill="none" stroke="var(--bg-3)" strokeWidth="1"/>
                      <circle
                        cx="16"
                        cy="16"
                        r="14"
                        fill="none"
                        stroke={progressColor}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeDasharray={2 * Math.PI * 14}
                        strokeDashoffset={(2 * Math.PI * 14) * (1 - Math.max(0, Math.min(100, progress)) / 100)}
                        style={{ transform: 'rotate(-90deg)', transformOrigin: '16px 16px', transition: 'stroke-dashoffset 0.7s linear, stroke 200ms linear' }}
                      />
                    </svg>
                  )}
                  {isAuthenticated && timeLeft !== null && timeLeft < 900 && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <Icon name="warn" size={13} style={{ color: progressColor }} />
                    </div>
                  )}
                </div>
                <div className="flex flex-col justify-center">
                  <span
                    className={`text-xs ${
                      isUserMenuOpen
                        ? "text-fg-2"
                        : currentMode === "Dark"
                        ? "text-fg-3"
                        : "text-fg-2"
                    }`}
                  >
                    {timeLeft !== null && timeLeft < 300 ? (
                      <span className="font-bold animate-pulse" style={{ color: "var(--danger)" }}>
                        Timeout: {Math.floor(timeLeft / 3600)}:{Math.floor((timeLeft % 3600) / 60).toString().padStart(2, '0')}:{(timeLeft % 60).toString().padStart(2, '0')}
                      </span>
                    ) : (
                      <span>Welcome back</span>
                    )}
                  </span>
                  <span
                    className={`font-bold text-sm ${
                      isUserMenuOpen
                        ? "text-fg-1"
                        : currentMode === "Dark"
                        ? "text-fg-1"
                        : "text-fg-2"
                    }`}
                  >
                    {user?.first_name || user?.name || "User"}
                  </span>
                </div>
              </div>
              <Icon
                name="chevron-down"
                size={16}
                className={`transition-transform duration-[var(--dur-fast)] ${
                  isUserMenuOpen
                    ? "text-fg-1 rotate-180"
                    : currentMode === "Dark"
                    ? "text-fg-3 group-hover:text-fg-1 rotate-0"
                    : "text-fg-2 group-hover:text-fg-2 rotate-0"
                }`}
              />

              
            </div>

            {/* User dropdown menu with real-time session countdown */}
            {isUserMenuOpen && (
              <div className="absolute right-0 mt-1 w-48 rounded-md shadow-lg bg-bg-2 border border-[var(--divider)] z-50 py-1 flex flex-col items-center">
                {/* Session countdown at top */}
                {isAuthenticated && timeLeft !== null && (
                  <div className="flex items-center gap-2 px-3 py-1 border-b border-[var(--divider)]" style={{maxWidth: '180px', margin: '0 auto'}}>
                    <div className="relative flex items-center justify-center" style={{height: '20px', width: '20px'}}>
                      <div
                        className="rounded-full flex items-center justify-center"
                        style={{
                          background: 'var(--bg-3)',
                          border: '1px solid var(--border)',
                          width: '14px',
                          height: '14px',
                          position: 'relative',
                          zIndex: 2
                        }}
                      >
                        <Icon name="user" size={9} style={{ color: 'var(--fg-2)' }} />
                      </div>
                      <svg
                        className="absolute top-0 left-0 pointer-events-none"
                        width="20" height="20" viewBox="0 0 20 20"
                        style={{ zIndex: 1 }}
                      >
                        <circle cx="10" cy="10" r="8" fill="none" stroke="var(--bg-3)" strokeWidth="0.7"/>
                        <circle
                          cx="10"
                          cy="10"
                          r="8"
                          fill="none"
                          stroke={progressColor}
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 8}
                          strokeDashoffset={(2 * Math.PI * 8) * (1 - Math.max(0, Math.min(100, progress)) / 100)}
                          style={{ transform: 'rotate(-90deg)', transformOrigin: '10px 10px', transition: 'stroke-dashoffset 0.7s linear, stroke 200ms linear' }}
                        />
                      </svg>
                    </div>
                    <div className="flex flex-col justify-center">
                      <span className="text-xs text-fg-2">Broker Account Session</span>
                      <span className="font-bold text-xs" style={{color: progressColor}}>
                        {Math.floor(timeLeft / 3600)}h {Math.floor((timeLeft % 3600) / 60)}m {(timeLeft % 60).toString().padStart(2, '0')}s
                      </span>
                    </div>
                  </div>
                )}
                {/* ...existing profile/settings/logout options... */}
                <div
                  className="flex items-center w-full px-4 py-2 text-sm text-fg-2 hover:bg-bg-3 cursor-pointer"
                  style={{justifyContent: 'flex-start'}}
                  onClick={handleProfileClick}
                >
                  <Icon name="user" size={16} className="mr-3" />
                  <span>Profile</span>
                </div>
                <div
                  className="flex items-center w-full px-4 py-2 text-sm text-fg-2 hover:bg-bg-3 cursor-pointer"
                  style={{justifyContent: 'flex-start'}}
                  onClick={handleThemeClick}
                >
                  <Icon name="settings" size={16} className="mr-3" />
                  <span>Theme</span>
                </div>
                <div
                  className="flex items-center w-full px-4 py-2 text-sm text-fg-2 hover:bg-bg-3 cursor-pointer"
                  style={{justifyContent: 'flex-start'}}
                  onClick={handleLogout}
                >
                  <Icon name="logout" size={16} className="mr-3" />
                  <span>Log out</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={onLoginClick}
            className="btn btn-primary"
          >
            Login
          </button>
        )}

        {/* ...existing code... (removed separate right-side icon) */}
      </div>


    </div>
  );
};

export default Navbar;
